import { useCallback, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { useTour } from "@providers/TourProvider";

type MicState = "checking" | "granted" | "prompt" | "denied";

/**
 * Tour step content that checks/requests microphone permissions before the recording steps.
 * If permissions are already granted, auto-advances to the next step.
 * Rendered inside ScoutTooltip as step content — uses advanceToNextStep to proceed.
 */
export default function MicPermissionGate() {
    const { advanceToNextStep, stopTour, markDismissed, stepIndex, activeTour } = useTour();
    const [micState, setMicState] = useState<MicState>("checking");

    // Check current permission state on mount
    useEffect(() => {
        let cancelled = false;
        navigator.permissions
            .query({ name: "microphone" as PermissionName })
            .then((result) => {
                if (cancelled) return;
                if (result.state === "granted") {
                    setMicState("granted");
                } else if (result.state === "denied") {
                    setMicState("denied");
                } else {
                    setMicState("prompt");
                }
            })
            .catch(() => {
                // permissions.query not supported — try requesting directly
                if (!cancelled) setMicState("prompt");
            });
        return () => {
            cancelled = true;
        };
    }, []);

    // Auto-advance when mic is already granted
    useEffect(() => {
        if (micState === "granted") {
            const timer = setTimeout(advanceToNextStep, 400);
            return () => clearTimeout(timer);
        }
    }, [micState, advanceToNextStep]);

    const requestMic = useCallback(async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            stream.getTracks().forEach((t) => t.stop());
            // Setting 'granted' triggers the useEffect above which auto-advances
            setMicState("granted");
        } catch (error) {
            if (error instanceof DOMException && error.name === "NotAllowedError") {
                setMicState("denied");
            } else {
                Sentry.captureException(error);
                setMicState("denied");
            }
        }
    }, []);

    if (micState === "checking") {
        return <span className="text-shadow-400">Checking microphone access...</span>;
    }

    if (micState === "granted") {
        return <span className="text-shadow-400">Microphone is ready — let's go!</span>;
    }

    if (micState === "denied") {
        return (
            <>
                <span className="text-shadow-400">
                    Your microphone is blocked. Click the <strong className="text-shadow-600">lock icon</strong> in your
                    address bar, set Microphone to <strong className="text-shadow-600">Allow</strong>, and refresh the
                    page.
                </span>
                <button
                    onClick={() => {
                        if (activeTour) markDismissed(activeTour.id, stepIndex);
                        stopTour();
                    }}
                    className="mt-2 w-full bg-shadow-100 text-shadow-600 rounded-lg px-4 py-2 text-sm font-semibold hover:bg-shadow-200 transition-colors focused"
                >
                    End Tour
                </button>
                <p className="mt-1 text-center text-[11px] text-shadow-400">Restart anytime from the Help menu</p>
            </>
        );
    }

    // prompt state
    return (
        <>
            <span className="text-shadow-400">
                We need microphone access to record. Your browser will ask for permission.
            </span>
            <button
                onClick={requestMic}
                className="mt-2 w-full bg-max-900 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-max-850 transition-colors focused"
            >
                Allow Microphone
            </button>
        </>
    );
}
